import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowRight, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [step, setStep] = useState("email"); // email | otp | done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();

  const startResendTimer = () => {
    setResendCooldown(60);
    const id = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      toast.success(`If ${email} is registered, we sent a 6-digit code.`);
      setStep("otp");
      startResendTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Enter the 6-digit code"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        password,
      });
      setStep("done");
      toast.success("Password reset successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-10 h-10 rounded-xl object-contain" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>InvoiceSetu</span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8">
          {step === "email" && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <Link to="/auth" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="back-to-login">
                  <ArrowLeft className="w-4 h-4" />
                </Link>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <KeyRound className="w-4 h-4" />
                  Reset Password
                </div>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Forgot your password?
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Enter the email you signed up with and we'll send you a 6-digit code.
              </p>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-sm font-semibold"
                  data-testid="send-reset-otp-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="flex items-center justify-center gap-2">
                      Send Reset Code <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </>
          )}

          {step === "otp" && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => setStep("email")} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="back-to-email">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <KeyRound className="w-4 h-4" />
                  Verify & set new password
                </div>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Enter code and new password
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. It expires in 10 minutes.
              </p>

              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Verification code</label>
                  <Input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="text-center text-2xl tracking-[0.5em] font-mono font-semibold"
                    data-testid="reset-otp-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="pl-10 pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm new password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      className="pl-10"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || otp.length < 6 || password.length < 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-sm font-semibold"
                  data-testid="reset-password-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="flex items-center justify-center gap-2">
                      Reset Password <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || loading}
                    onClick={handleSendOtp}
                    className="text-xs text-slate-500 hover:text-blue-600 disabled:text-slate-400 disabled:cursor-not-allowed"
                    data-testid="resend-reset-otp-btn"
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't get it? Resend code"}
                  </button>
                </div>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="text-center py-4" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Password reset!
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate("/auth")}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-sm font-semibold"
                data-testid="go-to-login-btn"
              >
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
